import { Calendar, ChevronDown } from "@icons/untitled_ui_icons";
import { nb } from "date-fns/locale";
import { t } from "i18next";
import moment from "moment";
import { CSSProperties, forwardRef, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { AriaButton } from "../../Buttons/AriaButton/AriaButton";
import InlineAddButton from "../../Buttons/InlineAddButton/InlineAddButton";
import styles from "./DateInput.module.css";
import "./DateInputStyles.css";
import { getCurrentNormalizedLanguageCode } from "@src/libs/i18next/language";

export interface DateInputProps {
  isButton?: boolean;
  date: Date | null;
  onChange: (date: Date | null) => void;
  isLabel?: boolean;
  disabled?: boolean;
  edit?: boolean;
  style?: CSSProperties;
  placeholder?: string;
}
type CustomInputProps = {
  edit?: boolean;
  value?: string;
  onClick?: () => void;
  className?: string;
  placeholder?: string;
  isButton?: boolean;
  isLabel?: boolean;
  onChange?: (value: any) => void;
  style?: CSSProperties;
};
export const DateInput = ({
  style,
  isButton = false,
  onChange: onChangeProp,
  date,
  isLabel = false,
  disabled,
  edit = true,
  placeholder,
}: DateInputProps) => {
  const [error, setError] = useState<boolean>(false);

  const handleChangeRaw = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!event.target.value) return;

    const formats = ["DD.MM.YYYY", "DD-MM-YYYY", "MMM D, YYYY", "MMM d yyyy"];
    const inputDate = moment(event.target.value, formats, true);

    if (inputDate.isValid()) {
      onChangeProp(inputDate.toDate());
      setError(false);
    } else {
      onChangeProp(null);
      setError(true);
    }
  };

  const onChange = (date: Date | null) => {
    onChangeProp(date);
    setError(false);
  };

  const locale = getCurrentNormalizedLanguageCode();

  return (
    <DatePicker
      disabled={disabled}
      selected={date}
      onChange={onChange}
      onChangeRaw={handleChangeRaw}
      dateFormat="MMM d, yyyy"
      customInput={
        <CustomInput
          style={style}
          className={`${styles.customInput} ${error ? styles.customInputError : ""}`}
          isButton={isButton}
          isLabel={isLabel}
          edit={edit}
          placeholder={placeholder}
        />
      }
      placeholderText={error ? t("main.invalidDate") : (placeholder ?? t("main.selectADate"))}
      calendarClassName={styles.calendarCustom}
      showPopperArrow={false}
      locale={locale}
    />
  );
};

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(({ style, ...props }, ref) => {
  const { value, onClick, className, placeholder, isButton, isLabel, onChange, edit } = props;
  if (isLabel) {
    return (
      <div style={{ display: "flex", alignContent: "center" }}>
        {!value && <InlineAddButton size="sm" onPress={onClick} />}
        <AriaButton variant="quiet" style={{ display: "flex", flexWrap: "nowrap", ...style }} onPress={onClick}>
          <input
            disabled={!value}
            value={value}
            onChange={onChange}
            ref={ref}
            style={{
              width: "105px",
              outline: "none",
              border: "none",
              margin: "5px 0",
              color: "var(--gray-700)",
              fontWeight: "500",
              backgroundColor: "transparent",
            }}
          />
        </AriaButton>
      </div>
    );
  } else {
    return (
      <button type="button" style={{ display: "flex", justifyContent: "space-between", ...style }} className={className} onClick={onClick}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <Calendar width={20} color="var(--gray-700)" />
          <input {...props} ref={ref} className={`${styles.innerInput} ${value ? "" : styles.inputNull}`} placeholder={placeholder} />
        </div>
        {!isButton && <ChevronDown width={20} color="var(--gray-700)" />}
      </button>
    );
  }
});
